import React, { useRef, useState } from "react";
import Footer from "../components/footer";
import { motion, AnimatePresence } from "framer-motion";
import emailjs from "@emailjs/browser";
import {
  FaPhoneAlt,
  FaWhatsapp,
  FaMapMarkerAlt,
  FaPaperPlane,
} from "react-icons/fa";
import { useLanguage } from "../components/idiomas";

export default function ContactPage() {
  const form = useRef();
  const { language, translations } = useLanguage();
  const t = translations[language].contact;

  const [status, setStatus] = useState(null); // success | error | null

  const sendEmail = (e) => {
    e.preventDefault();

    emailjs
      .sendForm(
        "service_o09984n",
        "template_a7mfbqr",
        form.current,
        "BB4mqjdADxxOhhTKI"
      )
      .then(
        () => {
          setStatus("success");
          form.current.reset();
          setTimeout(() => setStatus(null), 4000); // desaparece después de 4s
        },
        (error) => {
          console.error("Error:", error);
          setStatus("error");
          setTimeout(() => setStatus(null), 4000);
        }
      );
  };

  return (
    <div className="min-h-screen flex flex-col font-sans text-[#1E2E45] bg-gray-50 relative">
      {/* ✅ Mensaje visual animado */}
      <AnimatePresence>
        {status && (
          <motion.div
            key={status}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4 }}
            className={`fixed top-6 left-1/2 transform -translate-x-1/2 z-50 px-6 py-3 rounded-xl shadow-lg text-white text-sm font-medium ${
              status === "success" ? "bg-green-600" : "bg-red-600"
            }`}
          >
            {status === "success" ? t.success : t.error}
          </motion.div>
        )}
      </AnimatePresence>

      <section className="flex flex-col md:flex-row justify-center items-start max-w-7xl mx-auto px-6 md:px-10 py-16 gap-10">
        {/* Columna izquierda */}
        <motion.div
          className="flex-1"
          initial={{ opacity: 0, x: -50 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <div className="mb-4">
            <span className="bg-[#e7eef7] text-[#1E2E45] text-sm font-semibold px-3 py-1 rounded-full">
              {t.tagline}
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-4">{t.title}</h2>
          <p className="text-gray-600 mb-10 text-lg leading-relaxed">
            {t.subtitle}
          </p>

          {/* Tarjetas de contacto */}
          <motion.div
            className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-10"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            {/* Llamada */}
            <div className="bg-white shadow-md rounded-2xl p-6 text-center hover:shadow-lg transition">
              <div className="bg-blue-700/60 w-12 h-12 flex items-center justify-center rounded-xl mx-auto mb-3">
                <FaPhoneAlt className="text-[#1E2E45] text-xl" />
              </div>
              <h4 className="font-semibold mb-1">{t.cards.phone.title}</h4>
              <p className="text-gray-600 text-sm mb-2">{t.cards.phone.desc}</p>
              <a
                href="tel:(+506)8553-1009"
                className="text-blue-700/90 font-medium text-sm hover:underline"
              >
                +506 8553-1009
              </a>
            </div>

            {/* Chat */}
            <div className="bg-white shadow-md rounded-2xl p-6 text-center hover:shadow-lg transition">
              <div className="bg-[#25D366]/90 w-12 h-12 flex items-center justify-center rounded-xl mx-auto mb-3">
                <FaWhatsapp className="text-[#075E54] text-xl" />
              </div>
              <h4 className="font-semibold mb-1">{t.cards.chat.title}</h4>
              <p className="text-gray-600 text-sm mb-2">{t.cards.chat.desc}</p>
              <a
                href="https://wa.me/+50685531009"
                className="text-[#25D366] font-medium hover:underline"
              >
                {t.cards.chat.link}
              </a>
            </div>

            {/* Ubicación */}
            <div className="bg-white shadow-md rounded-2xl p-6 text-center hover:shadow-lg transition">
              <div className="bg-orange-300 w-12 h-12 flex items-center justify-center rounded-xl mx-auto mb-3">
                <FaMapMarkerAlt className="text-orange-500 text-xl" />
              </div>
              <h4 className="font-semibold mb-1">{t.cards.map.title}</h4>
              <p className="text-gray-600 text-sm mb-2">{t.cards.map.desc}</p>
              <a
                href="https://maps.app.goo.gl/Sy2NUoPmYBKwzGcP9"
                className="text-orange-500 font-medium hover:underline"
              >
                {t.cards.map.link}
              </a>
            </div>
          </motion.div>

          {/* Tarjetas grandes */}
          <motion.div
            className="grid grid-cols-1 sm:grid-cols-2 gap-6"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <div className="relative overflow-hidden rounded-2xl">
              <img
                src="/images/AsesoriaContacto.png"
                alt={t.images.asesoria}
                className="w-full h-48 object-cover transform hover:scale-105 transition duration-500"
              />
              <div className="absolute inset-0 bg-black/40 flex items-end p-4">
                <p className="text-white font-semibold">{t.images.asesoria}</p>
              </div>
            </div>

            <div className="relative overflow-hidden rounded-2xl">
              <img
                src="/images/AbogadoContacto.png"
                alt={t.images.abogado}
                className="w-full h-48 object-cover transform hover:scale-105 transition duration-500"
              />
              <div className="absolute inset-0 bg-black/40 flex items-end p-4">
                <p className="text-white font-semibold">{t.images.abogado}</p>
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Columna derecha - Formulario */}
        <motion.div
          className="flex-1 bg-white shadow-lg rounded-2xl p-10"
          initial={{ opacity: 0, x: 50 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h3 className="text-2xl font-semibold mb-4 text-[#1E2E45]">
            {t.form.title}
          </h3>
          <p className="text-gray-600 mb-8">{t.form.subtitle}</p>

          <form ref={form} onSubmit={sendEmail} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium mb-1 text-[#1E2E45]">
                  {t.form.nameLabel}
                </label>
                <input
                  required
                  name="name"
                  placeholder={t.form.placeholders.name}
                  className="w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-[#0B1C2C]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 text-[#1E2E45]">
                  {t.form.emailLabel}
                </label>
                <input
                  required
                  name="email"
                  type="email"
                  placeholder={t.form.placeholders.email}
                  className="w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-[#0B1C2C]"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium mb-1 text-[#1E2E45]">
                  {t.form.phoneLabel}
                </label>
                <input
                  name="phone"
                  placeholder={t.form.placeholders.phone}
                  className="w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-[#0B1C2C]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 text-[#1E2E45]">
                  {t.form.areaLabel}
                </label>
                <select
                  required
                  name="area"
                  className="w-full border border-gray-300 rounded-lg p-3 bg-white focus:outline-none focus:ring-2 focus:ring-[#0B1C2C]"
                >
                  <option value="">{t.form.select.default}</option>
                  <option>{t.form.select.civil}</option>
                  <option>{t.form.select.penal}</option>
                  <option>{t.form.select.notarial}</option>
                  <option>{t.form.select.contable}</option>
                  <option>{t.form.select.other}</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1 text-[#1E2E45]">
                {t.form.messageLabel}
              </label>
              <textarea
                required
                name="message"
                rows={5}
                placeholder={t.form.placeholders.message}
                className="w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-[#0B1C2C] resize-none"
              ></textarea>
            </div>

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="submit"
              className="w-full bg-gradient-to-r from-[#0B1C2C] to-[#1E3A8A] text-white py-3 rounded-lg font-semibold transition flex items-center justify-center gap-2"
            >
              <FaPaperPlane /> {t.form.button}
            </motion.button>

            <p className="text-xs text-gray-500 text-center">{t.form.disclaimer}</p>
          </form>
        </motion.div>
      </section>

      <Footer />
    </div>
  );
}
